import React, { useEffect, useMemo, useState } from "react"
import _ from "lodash"
import { useNavigate } from "react-router-dom"
import { Button, CircularProgress } from "@mui/material"
import {
    CheckCircle,
    DesignServicesOutlined,
    LockOutlined,
    ZoomInOutlined
} from "@mui/icons-material"
import { useLazyMarksheetTemplateActionQuery } from "../../Redux/actions/certificate.action"
import {
    useLazyGetInstitutionMetadataQuery,
    useUpdateInstitutionMetadataMutation,
    useCreateInstitutionMetadataMutation
} from "../../Redux/actions/setting.action"
import { useSnackbar } from "../../components/Snakebar"
import EmptyState from "../../Lectures/components/EmptyState"
import FullScreenImageViewer from "../../Result/component/TemplateView"
import { GetCurrentUser, userAllowingOperations } from "../../utils/hooks"

const buttonSx = { textTransform: "none", borderRadius: "8px", whiteSpace: "nowrap" }

function TemplateCard({ template, inUse, saving, canUpdate, onUse, onPreview }) {
    return (
        <li
            className={[
                "group flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm transition-shadow hover:shadow-md",
                inUse ? "border-indigo-500 ring-2 ring-indigo-500/20" : "border-slate-200"
            ].join(" ")}
        >
            <button
                type="button"
                onClick={onPreview}
                aria-label={`Preview ${template.name}`}
                className="relative aspect-[4/3] w-full overflow-hidden bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500"
            >
                <img
                    src={`data:image/png;base64,${template.image}`}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-contain p-3 transition-transform duration-300 group-hover:scale-[1.03]"
                />
                <span className="absolute inset-0 flex items-center justify-center bg-slate-900/0 opacity-0 transition group-hover:bg-slate-900/30 group-hover:opacity-100">
                    <span className="flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-800 shadow">
                        <ZoomInOutlined sx={{ fontSize: 16 }} /> Preview
                    </span>
                </span>
                {inUse && (
                    <span className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white shadow">
                        <CheckCircle sx={{ fontSize: 14 }} /> In use
                    </span>
                )}
            </button>

            <div className="flex flex-1 flex-col gap-3 border-t border-slate-100 p-4">
                <h3 className="truncate font-semibold text-slate-900" title={template.name}>{template.name}</h3>
                <div className="mt-auto flex gap-2">
                    <Button fullWidth size="small" variant="outlined" color="inherit" onClick={onPreview}
                        sx={{ ...buttonSx, borderColor: "#e2e8f0", color: "#334155" }}>
                        Preview
                    </Button>
                    <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        disableElevation
                        disabled={inUse || saving || !canUpdate}
                        onClick={onUse}
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                        sx={buttonSx}
                    >
                        {inUse ? "In use" : "Use template"}
                    </Button>
                </div>
            </div>
        </li>
    )
}

const TemplateSelector = () => {
    const navigate = useNavigate()
    const { showSnackbar } = useSnackbar()
    const platformId = useMemo(() => _.get(GetCurrentUser(), "meta_data.user_platform", null), [])
    const canUpdate = useMemo(() => userAllowingOperations().update, [])

    const [preview, setPreview] = useState(null)
    const [savingId, setSavingId] = useState(null)

    const [getTemplates, templatesState] = useLazyMarksheetTemplateActionQuery()
    const [getMetadata, metadataState] = useLazyGetInstitutionMetadataQuery()
    const [updateMetadata] = useUpdateInstitutionMetadataMutation()
    const [createMetadata] = useCreateInstitutionMetadataMutation()

    useEffect(() => {
        getTemplates()
        if (platformId) getMetadata(platformId)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // An expired session can't load templates; send the user to sign in again
    useEffect(() => {
        const detail = _.get(templatesState, "error.data.detail", "")
        if (templatesState.isError && String(detail).includes("expired token")) navigate("/login")
    }, [templatesState, navigate])

    const templates = useMemo(() => {
        const list = _.get(templatesState, "data.data", [])
        return Array.isArray(list) ? list : []
    }, [templatesState])

    const schoolMetadata = _.get(metadataState, "data.metadata.meta_data", null)
    const inUseId = _.get(schoolMetadata, "certificate_template.id", null)
    const inUseName = templates.find((template) => template.id === inUseId)?.name

    const applyTemplate = async (template) => {
        if (!platformId || savingId) return
        const certificate_template = { id: template.id, name: template.name }
        setSavingId(template.id)
        try {
            const result = schoolMetadata
                ? await updateMetadata({
                    platform_id: platformId,
                    meta_data: { ...schoolMetadata, certificate_template }
                }).unwrap()
                : await createMetadata({
                    platform_id: platformId,
                    meta_data: { certificate_template }
                }).unwrap()
            showSnackbar({
                message: _.get(result, "message") || `${template.name} is now your certificate template.`,
                severity: "success"
            })
            getMetadata(platformId)
        } catch {
            showSnackbar({ message: "Error while update metadata,Please try after sometime.", severity: "error" })
        } finally {
            setSavingId(null)
        }
    }

    const loading = templatesState.isLoading || (templatesState.isFetching && templates.length === 0)

    return (
        <div className="mx-auto w-full max-w-6xl">
            {/* Current choice */}
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-slate-500">
                    {inUseName ? (
                        <>Certificates are generated with <span className="font-semibold text-slate-900">{inUseName}</span>.</>
                    ) : metadataState.isFetching ? (
                        "Loading your current template…"
                    ) : (
                        "No template chosen yet. Pick one to use for all certificates."
                    )}
                </p>
                {!canUpdate && (
                    <span className="flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">
                        <LockOutlined sx={{ fontSize: 14 }} /> Only admins can change the template
                    </span>
                )}
            </div>

            {loading ? (
                <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {[0, 1, 2, 3].map((key) => (
                        <li key={key} className="h-[300px] animate-pulse rounded-2xl bg-slate-100" />
                    ))}
                </ul>
            ) : templatesState.isError || templates.length === 0 ? (
                <EmptyState
                    icon={<DesignServicesOutlined />}
                    title={templatesState.isError ? "Couldn't load templates" : "No templates available"}
                    description={
                        templatesState.isError
                            ? "Please check your connection and try again."
                            : "Certificate templates will appear here once they're added."
                    }
                    actionLabel={templatesState.isError ? "Retry" : undefined}
                    onAction={templatesState.isError ? () => getTemplates() : undefined}
                />
            ) : (
                <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {templates.map((template) => (
                        <TemplateCard
                            key={template.id}
                            template={template}
                            inUse={template.id === inUseId}
                            saving={savingId === template.id}
                            canUpdate={canUpdate && !savingId}
                            onUse={() => applyTemplate(template)}
                            onPreview={() => setPreview(template)}
                        />
                    ))}
                </ul>
            )}

            <FullScreenImageViewer
                open={Boolean(preview)}
                title={preview?.name || "Certificate Template Design"}
                onClose={() => setPreview(null)}
                imageUrl={preview?.image || ""}
            />
        </div>
    )
}

export default TemplateSelector
